import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ChatListItem from "../ChatListItem";
import { useChats } from "../../../../api/Chats/chats";
import NewConnectionModal from "./NewConnectionModal.jsx";

export default function ChatListUI() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const currentChat = params.get("chatId");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const loadMoreRef = useRef(null);

  const token = localStorage.token;
  let currentUserId = "";
  try {
    if (token) {
      const payload = JSON.parse(atob(token.split(".")[1]));
      currentUserId = payload.userid || payload._id || payload.id;
    }
  } catch (err) {
    // console.error("Failed to decode token", err);
  }

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useChats({ token, limit: 10, search: searchTerm });

  // ✅ FIX: Remove duplicates using useMemo
  const chats = useMemo(() => {
    const allChats = data?.pages?.flatMap((page) => page.chats) ?? [];
    
    // Remove duplicates by keeping track of seen IDs
    const seen = new Set();
    const uniqueChats = allChats.filter((chat) => {
      if (seen.has(chat._id)) {
        console.warn(`Duplicate chat found: ${chat._id}`);
        return false;
      }
      seen.add(chat._id);
      return true;
    });
    
    return uniqueChats;
  }, [data]);

  useEffect(() => {
    if (!hasNextPage || !loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage]);

  return (
    <div className="w-[400px] border-r h-screen flex flex-col bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header */}
      <div className="p-4 flex justify-between items-center border-b bg-white shadow-sm">
        <h2 className="text-xl font-bold text-gray-800">💬 Chats</h2>
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-8 h-8 bg-blue-100 text-blue-600 flex items-center justify-center rounded-full hover:bg-blue-200 transition"
          title="Start New Chat"
        >
          +
        </button>
      </div>

      {/* Search Box */}
      <div className="p-2 border-b bg-white">
        <input
          type="text"
          placeholder="Search chats..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-2">
        {isLoading && (
          <div className="p-4 text-gray-500 text-center animate-pulse">
            Loading chats...
          </div>
        )}
        {isError && (
          <div className="p-4 text-red-500 text-sm text-center">
            Failed to load chats.
          </div>
        )}

        {chats.map((chat) => {
          const isApproved = chat.approved !== false;
          return (
            <div
              key={chat._id}
              onClick={() => {
                if (isApproved) navigate(`/chats?chatId=${chat._id}`);
              }}
              className={`
                group rounded-2xl transition-all duration-200 shadow-sm
                ${
                  isApproved
                    ? "cursor-pointer bg-white hover:bg-blue-50 hover:shadow-md hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-300"
                    : "cursor-not-allowed bg-gray-200 text-gray-500 opacity-75"
                }
              `}
            >
              <ChatListItem
                chat={chat}
                currentUserId={currentUserId}
                currentChat={currentChat}
                isApproved={isApproved}
              />
            </div>
          );
        })}

        {!isLoading && chats.length === 0 && (
          <div className="p-4 text-gray-500 text-sm text-center">
            No chats found.
          </div>
        )}

        {/* Load More Trigger */}
        <div
          ref={loadMoreRef}
          className="h-10 flex justify-center items-center"
        >
          {isFetchingNextPage && (
            <span className="text-gray-500 text-sm">Loading more...</span>
          )}
        </div>
      </div>

      <NewConnectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelectUser={(user) => navigate(`/start-chat?userId=${user._id}`)}
      />
    </div>
  );
}